// Banner detail overview — tabs, progress steps, compliance health score.

import { ROUTES } from 'src/router/const'

export const BANNER_DETAIL_TAB_NAMES = {
  OVERVIEW: 'overview',
  COOKIES_TRACKERS: 'cookies-trackers',
  APPEARANCE: 'appearance',
  BEHAVIOUR: 'behaviour',
}

export const BANNER_DETAIL_TABS = [
  {
    key: BANNER_DETAIL_TAB_NAMES.OVERVIEW,
    label: 'Overview',
    icon: 'layout-dashboard',
    route: ROUTES.BANNER_DETAIL_OVERVIEW,
  },
  {
    key: BANNER_DETAIL_TAB_NAMES.COOKIES_TRACKERS,
    label: 'Cookies & Trackers',
    icon: 'scan-search',
    route: ROUTES.BANNER_DETAIL_COOKIES_TRACKERS,
  },
  {
    key: BANNER_DETAIL_TAB_NAMES.APPEARANCE,
    label: 'Appearance',
    icon: 'palette',
    route: ROUTES.BANNER_DETAIL_APPEARANCE,
  },
  {
    key: BANNER_DETAIL_TAB_NAMES.BEHAVIOUR,
    label: 'Behaviour',
    icon: 'route',
    route: ROUTES.BANNER_DETAIL_BEHAVIOUR,
  },
]

export const BANNER_DETAIL_TAB_KEY_BY_ROUTE = Object.fromEntries(
  BANNER_DETAIL_TABS.map(({ key, route }) => [route, key])
)

// -----------------------------------------------------------------------------
// Banner detail Overview tab - implementation progress + appearance defaults
//
// Four steps (Figma duplicate "Scan Website for Trackers" dropped - same as
// "Run Trackers Scan", both map to `lastScanned`). OverviewTab derives completion
// from banner detail + install status using `deriveFrom` on each step.

export const OVERVIEW_PROGRESS_STEP_KEYS = {
  CREATE_BANNER: 'create-banner',
  RUN_SCAN: 'run-scan',
  INSTALL_SNIPPET: 'install-snippet',
  STYLE_CUSTOMIZE: 'style-customize',
}

/** How OverviewTab derives each step's completed state. */
export const OVERVIEW_PROGRESS_DERIVE_FROM = {
  /** Always complete when banner exists; completed sub-label from `dateField`. */
  BANNER_CREATED: 'banner-created',
  /** Complete when `banner.lastScanned` is set; completed sub-label from `dateField`. */
  LAST_SCANNED: 'last-scanned',
  /** Complete when install status `state` is `installed`; uses `completedLabel`. */
  INSTALL_STATUS: 'install-status',
  /** Complete when appearance differs from `BANNER_DEFAULT_APPEARANCE`; uses `completedLabel`. */
  APPEARANCE: 'appearance',
}

/**
 * Ordered overview progress steps for the Banner detail Overview tab.
 * Date-based completed sub-labels use prefix "Completed on" + `formatDateByFormat(field, 'MMM d, yyyy')`.
 *
 * @type {Array<{
 *   key: string,
 *   label: string,
 *   headerTitle: string,
 *   icon: string,
 *   activeBadge?: string,
 *   pendingLabel: string,
 *   deriveFrom: string,
 *   dateField?: 'createdAt'|'lastScanned',
 *   completedLabel?: string,
 * }>}
 */
export const OVERVIEW_PROGRESS_STEPS = [
  {
    key: OVERVIEW_PROGRESS_STEP_KEYS.CREATE_BANNER,
    label: 'Create banner & domain',
    headerTitle: 'Banner Details',
    icon: 'file-text',
    pendingLabel: 'Create your banner to get started',
    deriveFrom: OVERVIEW_PROGRESS_DERIVE_FROM.BANNER_CREATED,
    dateField: 'createdAt',
  },
  {
    key: OVERVIEW_PROGRESS_STEP_KEYS.RUN_SCAN,
    label: 'Run Cookies Scan',
    headerTitle: 'Website Scan',
    icon: 'scan-search',
    activeBadge: 'Recommended next step',
    pendingLabel: 'Run a scan to detect cookies on your site',
    deriveFrom: OVERVIEW_PROGRESS_DERIVE_FROM.LAST_SCANNED,
    dateField: 'lastScanned',
  },
  {
    key: OVERVIEW_PROGRESS_STEP_KEYS.INSTALL_SNIPPET,
    label: 'Install snippet on website',
    headerTitle: 'Install Script',
    icon: 'code',
    activeBadge: 'Recommended next step',
    pendingLabel: 'Waiting for script detection on main domain',
    deriveFrom: OVERVIEW_PROGRESS_DERIVE_FROM.INSTALL_STATUS,
    completedLabel: 'Script detected',
  },
  {
    key: OVERVIEW_PROGRESS_STEP_KEYS.STYLE_CUSTOMIZE,
    label: 'Style & Customize',
    headerTitle: 'Style & Customize',
    icon: 'layout-panel-top',
    pendingLabel: 'Currently using the Default design - make it your own via the Appearance tab',
    deriveFrom: OVERVIEW_PROGRESS_DERIVE_FROM.APPEARANCE,
    completedLabel: 'Banner customized',
  },
]

// -----------------------------------------------------------------------------
// Banner detail Overview - compliance health score
//
// Weighted score (0–100): trackers 40%, install status 30%, scan freshness 25%,
// privacy policy 5%. Level thresholds apply to score as a percentage of max
// possible (see calcComplianceHealth in src/services/helpers/banner/compliance.helper.js).

/** Point weights per scoring factor (must sum to 100). */
export const COMPLIANCE_HEALTH_WEIGHTS = {
  TRACKERS: 40,
  INSTALL_STATUS: 30,
  SCAN_FRESHNESS: 25,
  PRIVACY_POLICY: 5,
}

/** Scan counts as fresh when lastScanned is within this many days. */
export const COMPLIANCE_HEALTH_SCAN_FRESHNESS_DAYS = 30

/** Level keys returned by getComplianceHealthLevel(). */
export const COMPLIANCE_HEALTH_LEVELS = {
  GOOD: 'good',
  MODERATE: 'moderate',
  POOR: 'poor',
}

/** Minimum score-as-percent-of-maxPossible for each level (GOOD ≥ 75%, MODERATE ≥ 40%). */
export const COMPLIANCE_HEALTH_THRESHOLDS = {
  GOOD: 75,
  MODERATE: 40,
}

/** Tooltip on the Overview banner step URL save button while a scan is running. */
export const BANNER_URL_SAVE_BLOCKED_WHILE_SCAN_TOOLTIP =
  'Wait for the scan to finish before changing the primary domain.'

/** Tooltip on the Overview compliance health card title help icon. */
export const COMPLIANCE_HEALTH_TOOLTIP =
  'We score your setup on cookie classification, banner installation, scan freshness, and privacy policy. ' +
  'Fix any highlighted items to improve your score.'

/**
 * Dynamic status subtitle for the Overview compliance card (ring shows the numeric score).
 * @param {{ level: string, failingCount: number }} params
 * @returns {string}
 */
export function getComplianceHealthStatusSubtitle({ level, failingCount }) {
  if (failingCount === 0) {
    return 'All checks passing'
  }

  if (failingCount === 1) {
    return level === COMPLIANCE_HEALTH_LEVELS.GOOD
      ? '1 item still needs attention'
      : '1 item needs attention'
  }

  const countLabel = `${failingCount} items`
  if (level === COMPLIANCE_HEALTH_LEVELS.POOR) {
    return `${countLabel} to fix`
  }

  return `${countLabel} need attention`
}

/** SVG viewBox size for the compliance score ring (matches 4rem rendered size). */
export const COMPLIANCE_HEALTH_SCORE_RING_SIZE = 64

export const COMPLIANCE_HEALTH_SCORE_RING_CENTER = COMPLIANCE_HEALTH_SCORE_RING_SIZE / 2

export const COMPLIANCE_HEALTH_SCORE_RING_STROKE = 4

/**
 * Path radius so a centered stroke reaches the viewBox edge (no inner gutter).
 * Outer stroke edge = center + radius + stroke/2 = size/2.
 */
export const COMPLIANCE_HEALTH_SCORE_RING_RADIUS =
  COMPLIANCE_HEALTH_SCORE_RING_CENTER - COMPLIANCE_HEALTH_SCORE_RING_STROKE / 2

/** Inner diameter inside the ring stroke - sized to the badge circle. */
export const COMPLIANCE_HEALTH_SCORE_RING_BADGE_SIZE =
  2 * (COMPLIANCE_HEALTH_SCORE_RING_RADIUS - COMPLIANCE_HEALTH_SCORE_RING_STROKE / 2)

export const COMPLIANCE_HEALTH_SCORE_RING_CIRCUMFERENCE =
  2 * Math.PI * COMPLIANCE_HEALTH_SCORE_RING_RADIUS

/**
 * Level labels and utility classes for the compliance score ring.
 * `ringClass` maps to SCSS modifiers on `.compliance-score`.
 * @type {Record<string, { label: string, textClass: string, bgClass: string, ringClass: string }>}
 */
export const COMPLIANCE_HEALTH_LEVEL_DISPLAY = {
  [COMPLIANCE_HEALTH_LEVELS.GOOD]: {
    label: 'Healthy',
    textClass: 'text-positive',
    bgClass: 'bg-positive-light',
    ringClass: 'compliance-score-good',
  },
  [COMPLIANCE_HEALTH_LEVELS.MODERATE]: {
    label: 'Needs Attention',
    textClass: 'text-cta-dark',
    bgClass: 'bg-cta-light',
    ringClass: 'compliance-score-moderate',
  },
  [COMPLIANCE_HEALTH_LEVELS.POOR]: {
    label: 'Needs Work',
    textClass: 'text-negative',
    bgClass: 'bg-negative-light',
    ringClass: 'compliance-score-poor',
  },
}

/** @type {import('src/services/consts/app.const').ConfirmToDeleteConfig} */
export const BANNER_CONFIRM_TO_DELETE = {
  title: 'No longer using this banner?',
  description:
    'If this banner is no longer needed, you can permanently delete it from your account.',
  consequence: "You'll lose access to its settings and data, and this cannot be undone.",
  buttonLabel: 'Delete banner',
  confirmTitle: 'Delete banner',
  confirmBody: 'This banner will be permanently deleted and you will lose access to it.',
  confirmLabel: 'Delete',
  confirmNote: "Don't worry - your account records and compliance history are retained.",
  inputId: 'delete-banner-confirm-name',
}
